import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiBriefcase,
  FiGlobe,
  FiLayers,
  FiTrendingUp,
  FiCompass,
  FiCheckCircle,
  FiArrowRight,
} from 'react-icons/fi';
import { Page, LayoutComposer } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './BecomeAgentPage.module.css';

const PILLARS = [
  {
    icon: <FiGlobe />,
    title: 'International Buyer Acquisition',
    text: 'ChinaDolphins markets products and manufacturing opportunities directly to overseas buyers, driving verified RFQs to your assigned factories without sales overhead.',
  },
  {
    icon: <FiLayers />,
    title: 'Assigned Factory Network',
    text: 'Approved Agents represent one or more verified manufacturing partners. Each factory SKU is assigned to one designated active Sourcing Agent.',
  },
  {
    icon: <FiTrendingUp />,
    title: 'Order Aggregation Multiplier',
    text: 'Smaller buyer requirements are pooled together to meet factory MOQs, transforming fragmented inquiries into large, lucrative production runs.',
  },
  {
    icon: <FiCompass />,
    title: 'Local Operational Leadership',
    text: 'You remain the essential professional on the ground in China, commanding respect, structured fees, and milestone commissions on every successfully executed order.',
  },
];

const RESPONSIBILITIES = [
  'Supplier & Factory Vetting and Legal Licence Verification',
  'Direct Buyer Communication & Specification Alignment',
  'Product Sourcing & Custom RFQ Price Tier Optimization',
  'Sample Production Oversight & International Express Courier Handling',
  'On-Site Factory Production Milestone Monitoring',
  'Rigorous Pre-Shipment Quality Control (QC) & Inspection Photo Reports',
  'China Customs Export Clearance & Bill of Lading (B/L) Handling',
  'Shipping & Container Loading Supervision',
];

const BecomeAgentPage = () => {
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <Page
      title="Become a Verified Sourcing Agent | ChinaDolphins"
      description="Join ChinaDolphins as a verified sourcing professional or agency. Access high-volume international buyers and represent verified Chinese factories."
      className={css.root}
    >
      <LayoutComposer areas={layoutAreas}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header">
                <TopbarContainer />
              </Topbar>

              <Main as="main" id="main-content">
                {/* ── Hero ── */}
                <section className={css.hero}>
                  <div className={css.heroInner}>
                    <div className={css.heroBadge}>
                      <FiBriefcase /> Partner Network for Mainland Sourcing Professionals
                    </div>
                    <h1 className={css.heroTitle}>
                      Get High-Volume Global Clients Without Building Your Own Sales Network
                    </h1>
                    <p className={css.heroSubtitle}>
                      Are you an independent sourcing professional or agency located in one of China&apos;s industrial hubs? ChinaDolphins connects approved local agents with international buyers looking for expert sourcing, verification, and quality oversight.
                    </p>
                    <Link to="/signup/agent" className={css.heroBtn}>
                      Apply to Become a Verified Agent <FiArrowRight />
                    </Link>
                  </div>
                </section>

                <section className={css.section}>
                  {/* ── 4 Pillars ── */}
                  <div className={css.sectionHeader}>
                    <div className={css.sectionTag}>Agent Benefits</div>
                    <h2 className={css.sectionTitle}>How ChinaDolphins Empowers Sourcing Agents</h2>
                  </div>

                  <div className={css.pillarGrid}>
                    {PILLARS.map((p, idx) => (
                      <div key={idx} className={css.pillarCard}>
                        <div className={css.pillarIcon}>{p.icon}</div>
                        <h3 className={css.pillarTitle}>{p.title}</h3>
                        <p className={css.pillarText}>{p.text}</p>
                      </div>
                    ))}
                  </div>

                  {/* ── Responsibilities Checklist ── */}
                  <div className={css.checklistCard}>
                    <div className={css.sectionHeader} style={{ marginBottom: 20 }}>
                      <div className={css.sectionTag}>Operational Scope</div>
                      <h2 className={css.sectionTitle}>Your Role on the Ground</h2>
                      <p style={{ color: '#64748b', margin: 0 }}>
                        As an authorized ChinaDolphins Sourcing Agent, you act as the trusted representative for global buyers and factories.
                      </p>
                    </div>

                    <div className={css.checklistGrid}>
                      {RESPONSIBILITIES.map((resp, idx) => (
                        <div key={idx} className={css.checklistItem}>
                          <FiCheckCircle className={css.checkIcon} />
                          <span>{resp}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Bottom CTA Box ── */}
                  <div className={css.ctaBox}>
                    <h2 className={css.ctaTitle}>Ready to Grow Your Sourcing Business?</h2>
                    <p className={css.ctaText}>
                      ChinaDolphins reviews all Agent applications before granting access to commercial activities. Submit your business information today.
                    </p>
                    <Link to="/signup/agent" className={css.heroBtn}>
                      Start Agent Application <FiArrowRight />
                    </Link>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

export default BecomeAgentPage;
